/**
 * User info card with password change form (ユーザ情報).
 * No API calls; receives form control and handler from page.
 */

import React from 'react';
import { Control, Controller, FieldErrors, UseFormHandleSubmit } from 'react-hook-form';
import { CollapsibleCard } from '@/components/molecules';
import AppButton from '@/components/atoms/AppButton';
import AppTextField from '@/components/atoms/AppTextField';
import type { ChangePasswordFormData } from '@/schemas/clinicInformationSchema';

export interface ClinicInformationUserSectionProps {
  loginId: string | undefined;
  control: Control<ChangePasswordFormData>;
  errors: FieldErrors<ChangePasswordFormData>;
  onSubmit: ReturnType<UseFormHandleSubmit<ChangePasswordFormData>>;
  isPending: boolean;
}

const ClinicInformationUserSection: React.FC<ClinicInformationUserSectionProps> = ({
  loginId,
  control,
  errors,
  onSubmit,
  isPending,
}) => (
  <CollapsibleCard title="ユーザ情報">
    <form onSubmit={onSubmit}>
      <div className="space-y-3">
        <div className="flex items-center">
          <label htmlFor="clinic-info-login-id" className="w-1/6 font-medium text-gray-700">
            ユーザーID
          </label>
          <div id="clinic-info-login-id" className="flex-1">
            {loginId}
          </div>
        </div>

        <div className="flex items-start">
          <label htmlFor="clinic-info-password" className="w-1/6 pt-1 font-medium text-gray-700">
            ログインパスワード
          </label>
          <div className="flex flex-col flex-1 gap-1">
            <Controller
              name="password"
              control={control}
              render={({ field, fieldState }) => (
                <AppTextField
                  {...field}
                  id="clinic-info-password"
                  type="password"
                  className="w-1/3"
                  status={fieldState.error ? 'error' : undefined}
                />
              )}
            />
            {errors.password && (
              <div className="text-sm text-red-500">{errors.password.message}</div>
            )}
          </div>
        </div>

        <div className="flex items-start">
          <label
            htmlFor="clinic-info-password-confirm"
            className="w-1/6 pt-1 font-medium text-gray-700"
          >
            ログインパスワード確認
          </label>
          <div className="flex flex-col flex-1 gap-1">
            <Controller
              name="password_confirmation"
              control={control}
              render={({ field, fieldState }) => (
                <AppTextField
                  {...field}
                  id="clinic-info-password-confirm"
                  type="password"
                  className="w-1/3"
                  status={fieldState.error ? 'error' : undefined}
                />
              )}
            />
            {errors.password_confirmation && (
              <div className="text-sm text-red-500">{errors.password_confirmation.message}</div>
            )}
          </div>
        </div>

        <div className="flex items-center">
          <div className="w-1/6" />
          <AppButton type="primary" htmlType="submit" loading={isPending} disabled={isPending}>
            {isPending ? '更新中...' : '更新'}
          </AppButton>
        </div>
      </div>
    </form>
  </CollapsibleCard>
);

export default ClinicInformationUserSection;
